import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, MapPin, ArrowLeft, ArrowRight, Spinner } from "@phosphor-icons/react";
import { storiesApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import MediaUploader from "@/components/MediaUploader";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Stories() {
    const { t } = useTranslation();
    const [groups, setGroups] = useState([]);
    const [activeGroupIdx, setActiveGroupIdx] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const { user } = useAuth() || {};

    const load = () => storiesApi.list().then(setGroups).catch(() => {});
    useEffect(() => { load(); }, []);

    return (
        <div className="mb-8" data-testid="stories-row">
            <div className="overline mb-3">{t("stories.title")}</div>
            <div className="flex gap-4 overflow-x-auto pb-2">
                {/* Add story */}
                {user && (
                    <button onClick={() => setShowCreate(true)} className="shrink-0 group" data-testid="story-add-btn">
                        <div className="w-20 h-20 rounded-full grid place-items-center relative"
                            style={{ background: "rgba(212,175,55,0.08)", border: "2px dashed rgba(212,175,55,0.4)" }}>
                            <Plus size={20} color="#D4AF37" weight="bold" />
                        </div>
                        <div className="text-[10px] tracking-[0.15em] uppercase text-white/60 text-center mt-2">{t("stories.add")}</div>
                    </button>
                )}
                {groups.map((g, i) => (
                    <button key={g.user_id} onClick={() => setActiveGroupIdx(i)} className="shrink-0" data-testid={`story-group-${g.user_handle}`}>
                        <div className="relative w-20 h-20 rounded-full p-[2px]" style={{ background: "linear-gradient(135deg, #D4AF37 0%, #E8DCC4 50%, #D4AF37 100%)" }}>
                            <img src={g.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${g.user_handle}`}
                                alt={g.user_handle}
                                className="w-full h-full rounded-full object-cover"
                                style={{ border: "2px solid var(--navy)" }} />
                            <div className="absolute -bottom-1 -right-1 rounded-full w-6 h-6 grid place-items-center text-[10px] font-medium"
                                style={{ background: "var(--gold)", color: "var(--navy)" }}>
                                {g.stories.length}
                            </div>
                        </div>
                        <div className="text-[10px] tracking-[0.15em] uppercase text-white/60 text-center mt-2 truncate max-w-[80px]">@{g.user_handle}</div>
                    </button>
                ))}
                {groups.length === 0 && !user && (
                    <div className="text-white/40 text-sm">{t("stories.noStories")}</div>
                )}
            </div>

            <StoryViewer groups={groups} startIdx={activeGroupIdx} onClose={() => setActiveGroupIdx(null)} />
            <StoryCreator open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
        </div>
    );
}

function StoryViewer({ groups, startIdx, onClose }) {
    const [gi, setGi] = useState(0);
    const [si, setSi] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => {
        if (startIdx !== null && startIdx !== undefined) {
            setGi(startIdx); setSi(0); setIsLoaded(false);
        }
    }, [startIdx]);

    useEffect(() => {
        if (startIdx === null || startIdx === undefined) return;
        if (imgRef.current && imgRef.current.complete) {
            setIsLoaded(true);
        }
        if (!isLoaded) return;
        const t = setTimeout(() => {
            next();
        }, 5500);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [gi, si, startIdx, isLoaded]);

    if (startIdx === null || startIdx === undefined || !groups[gi]) return null;
    const group = groups[gi];
    const story = group.stories[si];

    const next = () => {
        setIsLoaded(false);
        if (si + 1 < group.stories.length) return setSi(si + 1);
        if (gi + 1 < groups.length) { setGi(gi + 1); setSi(0); }
        else onClose();
    };
    const prev = () => {
        setIsLoaded(false);
        if (si > 0) return setSi(si - 1);
        if (gi > 0) { setGi(gi - 1); setSi(0); }
    };

    return (
        <AnimatePresence>
            {story && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[95] grid place-items-center"
                    style={{ background: "rgba(0,0,0,0.95)" }}
                    onClick={onClose}
                    data-testid="story-viewer"
                >
                    <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md h-full md:h-[85vh] md:rounded-3xl overflow-hidden" style={{ background: "black" }}>
                        {/* progress bars */}
                        <div className="absolute top-3 left-3 right-3 z-10 flex gap-1">
                            {group.stories.map((_, i) => (
                                <div key={i} className="flex-1 h-0.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.2)" }}>
                                    <motion.div
                                        key={`${gi}-${si}-${i}`}
                                        initial={{ width: i < si ? "100%" : "0%" }}
                                        animate={{ width: i < si ? "100%" : (i === si && isLoaded ? "100%" : "0%") }}
                                        transition={{ duration: i === si && isLoaded ? 5.5 : 0, ease: "linear" }}
                                        className="h-full"
                                        style={{ background: "white" }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* header */}
                        <div className="absolute top-8 left-4 right-4 z-30 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <img src={group.user_avatar} alt={group.user_handle} className="w-8 h-8 rounded-full object-cover" />
                                <div>
                                    <div className="text-sm text-white">@{group.user_handle}</div>
                                    {story.country && (
                                        <div className="text-[10px] text-white/60 flex items-center gap-1"><MapPin size={9} /> {story.country}</div>
                                    )}
                                </div>
                            </div>
                            <button onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="story-close">
                                <X size={14} color="white" />
                            </button>
                        </div>

                        {/* media */}
                        {!isLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center z-0">
                                <Spinner size={32} color="var(--gold)" className="animate-spin" />
                            </div>
                        )}
                        {story.media_kind === "video" ? (
                            <video 
                                src={story.media_url} 
                                autoPlay 
                                muted 
                                playsInline 
                                className="w-full h-full object-contain relative z-10" 
                                style={{ opacity: isLoaded ? 1 : 0, transition: "opacity 0.3s" }}
                                onLoadedData={() => setIsLoaded(true)}
                                onError={() => setIsLoaded(true)}
                            />
                        ) : (
                            <img 
                                ref={imgRef}
                                src={story.media_url} 
                                alt="" 
                                className="w-full h-full object-contain relative z-10" 
                                style={{ opacity: isLoaded ? 1 : 0, transition: "opacity 0.3s" }}
                                onLoad={() => setIsLoaded(true)}
                                onError={() => setIsLoaded(true)}
                            />
                        )}

                        {story.caption && (
                            <div className="absolute bottom-6 left-4 right-4 text-center">
                                <div className="inline-block glass rounded-full px-4 py-2 text-sm text-white">
                                    {story.caption}
                                </div>
                            </div>
                        )}

                        {/* nav */}
                        <button onClick={prev} className="absolute left-0 top-0 bottom-0 w-1/3 z-20" aria-label="prev" data-testid="story-prev" />
                        <button onClick={next} className="absolute right-0 top-0 bottom-0 w-1/3 z-20" aria-label="next" data-testid="story-next" />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function StoryCreator({ open, onClose, onCreated }) {
    const { t } = useTranslation();
    const [media, setMedia] = useState(null);
    const [caption, setCaption] = useState("");
    const [country, setCountry] = useState("");
    const [busy, setBusy] = useState(false);
    const [mediaUploading, setMediaUploading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!media?.url) return toast.error(t("stories.toasts.addMedia"));
        setBusy(true);
        try {
            await storiesApi.create({
                media_url: media.url,
                media_kind: media.kind,
                caption: caption || null,
                country: country || null,
            });
            setMedia(null); setCaption(""); setCountry("");
            toast.success(t("stories.toasts.posted"));
            onCreated?.();
        } catch (err) {
            toast.error(t("stories.toasts.failed"));
        } finally { setBusy(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] flex items-center justify-center p-4"
                    style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                    onClick={onClose}
                    data-testid="story-create-modal"
                >
                    <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20 }}
                        className="rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                    >
                        <div className="overline mb-2">{t("stories.creator.overline")}</div>
                        <h3 className="font-serif text-3xl text-white mb-6">{t("stories.creator.title")}</h3>
                        <div className="space-y-4">
                            <MediaUploader value={media} onChange={setMedia} onBusyChange={setMediaUploading} label={t("stories.creator.mediaLabel")} accept="both" maxDuration={15} testId="story-media" />
                            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t("stories.creator.captionPlaceholder")} className="input" data-testid="story-caption" />
                            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t("stories.creator.countryPlaceholder")} className="input" data-testid="story-country" />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">{t("common.cancel")}</button>
                            <button type="submit" disabled={busy || mediaUploading} className="btn-gold flex-1 justify-center" data-testid="story-save">
                                {busy ? t("stories.creator.posting") : mediaUploading ? t("stories.creator.uploading") : t("stories.addStory")}
                            </button>
                        </div>
                        <style>{`.input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; } .input::placeholder { color: rgba(255,255,255,0.35); } .input:focus { outline: none; border-color: var(--gold); }`}</style>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
